import { VelocityEnv } from '@velocity-exchange/sdk';
import { useCallback } from 'react';
import { useGetClientsForNewConnection } from './useGetClientsForNewConnection';
import { useSubscribeVelocityClient } from './useSubscribeVelocityClient';
import { useHandleSubscriptionSuccess } from './useHandleSubscriptionSuccess';
import { useUnsubscribeClients } from './useUnsubscribeClients';
import { RpcEndpoint } from '@velocity-exchange/common';
import { SubscriberType } from 'src/environmentVariables/EnvironmentVariables';

/**
 * `superseded` means a newer run started before this one finished, so this run's
 * client was discarded. It is not a connection failure and must not trigger the
 * caller's retry/notification handling.
 */
export type ConnectionStepsResult = 'success' | 'failed' | 'superseded';

/**
 * Identifies the newest connection attempt. Every run builds its own
 * `VelocityClient`, but only the newest may be published to the store: a client
 * created before the wallet was connected/emulated never loads its `User`s
 * (`skipLoadUsers` is only cleared by `updateWallet`/`emulateAccount`), so
 * committing a stale one leaves the store with an empty `users` map while the
 * wallet's real client is orphaned — subscriptions alive, UI empty.
 *
 * Module-scoped so it orders runs app-wide rather than per hook instance.
 */
let latestRunToken = 0;

export const useRunConnectionSteps = () => {
	const { getClientsForNewConnection } = useGetClientsForNewConnection();
	const { subscribeToVelocityClientData } = useSubscribeVelocityClient();
	const { handleSubscriptionSuccess } = useHandleSubscriptionSuccess();
	const { unsubscribeClients } = useUnsubscribeClients();

	const runConnectionSteps = useCallback(
		async (
			newRpc: RpcEndpoint,
			subscriberType: SubscriberType,
			env: VelocityEnv
		): Promise<ConnectionStepsResult> => {
			const runToken = ++latestRunToken;

			try {
				await unsubscribeClients();

				const newClients = getClientsForNewConnection(
					newRpc,
					subscriberType,
					env
				);

				const connectionResult = await subscribeToVelocityClientData(
					newClients.newVelocityClient
				);

				if (runToken !== latestRunToken) {
					// Tear this client down rather than leaking it — it is subscribed by
					// now, and an orphaned client keeps its RPC/websocket traffic going.
					await newClients.newVelocityClient
						.unsubscribe()
						.catch((e: unknown) =>
							console.error('Failed to unsubscribe superseded client', e)
						);
					return 'superseded';
				}

				if (connectionResult) {
					handleSubscriptionSuccess({
						selectedRpc: newRpc,
						...newClients,
					});
				}

				return connectionResult ? 'success' : 'failed';
			} catch (e: any) {
				console.log(`Caught error running connection steps`);
				console.error(e);
				return 'failed';
			}
		},
		[
			getClientsForNewConnection,
			subscribeToVelocityClientData,
			handleSubscriptionSuccess,
			unsubscribeClients,
		]
	);

	return { runConnectionSteps };
};
